import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDecision } from "@optimizely/react-sdk";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import {
  Photo,
  Video,
  Trivia,
  MultipleChoice,
  StickerPhoto,
  Text,
  QRCode,
  AugmentedRealityPhoto,
  AugmentedRealityVideo,
  BackgroundPhoto,
  BackgroundVideo,
  SplitPath,
} from "../task-types";
import { useQueryNoInvalidate, useQueryInvalidateInfinite } from "../Organize.state";
import {
  getTypeSelectionWrites,
  isPromptDrivenTaskType,
  persistedSubtypeForTypeSelection,
  subtypeFromPrompt,
} from "../transformationPrompt";
import { getUploadedDefaultOverlayPayload } from "../defaultBackgroundOverlay";
import { alertState } from "../../common/AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../../lib/error";
import {
  ChallengeTypeBody,
  ChallengeTypeExample,
  ChallengeTypeHeader,
  ChallengeTypeIcon,
  ChallengeTypeMeta,
  ChallengeTypeOption,
  ChallengeTypesWrapper,
  MoreTypePill,
  MoreTypesRow,
  SectionLabel,
  TypeGroupLabel,
} from "./TypeSection.styles";

import { ReactComponent as IconStandardPhoto } from "../../images/challenges/standard-photo.svg";
import { ReactComponent as IconAiPhoto } from "../../images/challenges/ai-photo-transformation.svg";
import { ReactComponent as IconText } from "../../images/challenges/text.svg";
import { ReactComponent as IconAiText } from "../../images/challenges/ai-text-transformation.svg";
import { ReactComponent as IconQrCode } from "../../images/challenges/qr-code.svg";
import { ReactComponent as IconStandardVideo } from "../../images/challenges/standard-video.svg";
import { ReactComponent as IconTrivia } from "../../images/challenges/trivia.svg";
import { ReactComponent as IconMultipleChoice } from "../../images/challenges/multiple-choice.svg";
import { ReactComponent as IconSplitPath } from "../../images/challenges/split-path.svg";
import { ReactComponent as IconPhotoInpainting } from "../../images/challenges/photo-inpainting.svg";
import { ReactComponent as IconVideoInpainting } from "../../images/challenges/video-inpainting.svg";
import { ReactComponent as IconAnimatedGif } from "../../images/challenges/animated-gif.svg";
import { ReactComponent as IconAudio } from "../../images/challenges/audio.svg";
import { ReactComponent as IconStickerPhoto } from "../../images/challenges/sticker-photo.svg";

import BannerStandardPhoto from "../../images/challenges-banners/standard-photo-challenge.png";
import BannerAiPhoto from "../../images/challenges-banners/ai-photo-transformation-challenge.png";
import BannerText from "../../images/challenges-banners/standard-text-challenge.png";
import BannerAiText from "../../images/challenges-banners/ai-text-transformation-challenge.png";
import BannerQrCode from "../../images/challenges-banners/qr-code-challenge.png";
import BannerStandardVideo from "../../images/challenges-banners/standard-video-challenge.png";
import BannerTrivia from "../../images/challenges-banners/trivia-challenge.png";
import BannerMultipleChoice from "../../images/challenges-banners/multiplechoice-challenge.png";
import BannerSplitPath from "../../images/challenges-banners/split-path-challenge.png";
import BannerPhotoInpainting from "../../images/challenges-banners/photo-inpainting-challenge.png";
import BannerVideoInpainting from "../../images/challenges-banners/video-inpainting-challenge.png";
import BannerAnimatedGif from "../../images/challenges-banners/animated-gif-challenge.png";
import BannerAudio from "../../images/challenges-banners/audio-challenge.png";
import BannerStickerPhoto from "../../images/challenges-banners/sticker-photo-challenge.png";
import DefaultBackgroundImage from "../../images/sample-background.png";

const TypeSection = ({ useQuery, id: challengeId }) => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useQueryNoInvalidate_ = useCallback(() => useQueryNoInvalidate(challengeId), [challengeId]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useQueryInvalidateInfinite_ = useCallback(() => useQueryInvalidateInfinite(challengeId), [challengeId]);
  const [{ id, realities, task_type, task_subtype, prompt, assets, overlay_media_uuid }, setChallenge] = useQueryInvalidateInfinite_();
  const setAlert = useSetRecoilState(alertState);
  const [decisionAr] = useDecision('ar')
  const [selectionIntent, setSelectionIntent] = useState({ challengeId: id, typeId: null });

  if (selectionIntent.challengeId !== id) {
    setSelectionIntent({ challengeId: id, typeId: null });
  }

  const popularTypes = useMemo(() => [
    {
      id: "photo-standard",
      taskType: 1,
      subtype: 0,
      label: t("Standard Photo"),
      description: t("Submit a photo."),
      details: t("Players submit a photo."),
      icon: IconStandardPhoto,
      image: BannerStandardPhoto,
      children: <Photo useQuery={useQueryNoInvalidate_} />,
      reset: { prompt: "" },
    },
    {
      id: "photo-ai",
      taskType: 1,
      subtype: 1,
      label: t("AI Photo Transformation"),
      description: t("Submit a photo, and AI transforms it into something new."),
      details: t("Players submit a photo, and AI transforms it using your prompt."),
      icon: IconAiPhoto,
      image: BannerAiPhoto,
      children: <Photo useQuery={useQueryNoInvalidate_} showTransformations />,
    },
    {
      id: "text-standard",
      taskType: 0,
      subtype: 0,
      label: t("Text"),
      description: t("Submit a text response."),
      details: t("Players submit a written response."),
      icon: IconText,
      image: BannerText,
      children: <Text useQuery={useQueryNoInvalidate_} />,
      reset: { prompt: "" },
    },
    {
      id: "text-ai",
      taskType: 0,
      subtype: 1,
      label: t("AI Text Transformation"),
      description: t("Submit a text response, and AI transforms it into an image."),
      details: t("Players submit a text response that AI turns into an image."),
      icon: IconAiText,
      image: BannerAiText,
      children: <Text useQuery={useQueryNoInvalidate_} />,
    },
    {
      id: "qr-code",
      taskType: 4,
      label: t("QR Code"),
      description: t("Scan a QR code to complete the challenge."),
      details: t("Players scan a QR code to complete the challenge."),
      icon: IconQrCode,
      image: BannerQrCode,
      children: <QRCode useQuery={useQueryNoInvalidate_} game_id={game_id} id={id} />,
    },
    {
      id: "video-standard",
      taskType: 2,
      subtype: 0,
      defaultVideoLength: 30,
      label: t("Standard Video"),
      description: t("Submit a video recording."),
      details: t("Players submit a video recording."),
      icon: IconStandardVideo,
      image: BannerStandardVideo,
      children: <Video useQuery={useQueryNoInvalidate_} showSharing={false} />,
    },
    {
      id: "trivia",
      taskType: 5,
      label: t("Trivia"),
      description: t("Submit a text response that is checked against valid answers."),
      details: t("Players submit an answer that is checked against valid responses."),
      icon: IconTrivia,
      image: BannerTrivia,
      children: <Trivia useQuery={useQueryNoInvalidate_} />,
    },
    {
      id: "multiple-choice",
      taskType: 6,
      label: t("Multiple Choice"),
      description: t("Select an answer from multiple options."),
      details: t("Players choose one answer from a list of options."),
      icon: IconMultipleChoice,
      image: BannerMultipleChoice,
      children: <MultipleChoice useQuery={useQueryNoInvalidate_} />,
    },
    {
      id: "split-path",
      taskType: 11,
      label: t("Split Path"),
      description: t("Select one path from the choices provided."),
      details: t("Players choose one path from the choices provided."),
      icon: IconSplitPath,
      image: BannerSplitPath,
      children: <SplitPath useQuery={useQueryInvalidateInfinite_} />,
    },
  ], [game_id, id, t, useQueryInvalidateInfinite_, useQueryNoInvalidate_]);

  const moreTypes = useMemo(() => [
    {
      id: "photo-inpainting",
      taskType: 9,
      label: t("Photo Inpainting"),
      description: t("Submit a photo, and AI swaps in your uploaded background."),
      details: t("Players submit a photo that is transformed with your uploaded background."),
      icon: IconPhotoInpainting,
      image: BannerPhotoInpainting,
      defaultOverlay: true,
      children: <BackgroundPhoto useQuery={useQueryNoInvalidate_} />,
    },
    {
      id: "video-inpainting",
      taskType: 10,
      label: t("Video Inpainting"),
      description: t("Submit a video, and AI swaps in your uploaded background."),
      details: t("Players submit a video that is transformed with your uploaded background."),
      icon: IconVideoInpainting,
      image: BannerVideoInpainting,
      defaultOverlay: true,
      children: <BackgroundVideo useQuery={useQueryNoInvalidate_} />,
    },
    {
      id: "animated-gif",
      taskType: 2,
      subtype: 1,
      defaultVideoLength: 5,
      label: t("Animated GIF"),
      description: t("Submit a video recording."),
      details: t("Players submit a video recording that is turned into an animated GIF."),
      icon: IconAnimatedGif,
      image: BannerAnimatedGif,
      children: <Video useQuery={useQueryNoInvalidate_} showSharing={false} />,
    },
    {
      id: "audio",
      taskType: 2,
      subtype: 2,
      defaultVideoLength: 10,
      label: t("Audio"),
      description: t("Submit a video recording from which audio will be extracted."),
      details: t("Players submit a video recording from which audio will be extracted."),
      icon: IconAudio,
      image: BannerAudio,
      children: <Video useQuery={useQueryNoInvalidate_} showSharing={false} />,
    },
    {
      id: "sticker-photo",
      taskType: 3,
      label: t("Sticker + Photo"),
      description: t("Submit a photo."),
      details: t("Players submit a photo and add your uploaded sticker to their image."),
      icon: IconStickerPhoto,
      image: BannerStickerPhoto,
      children: <StickerPhoto useQuery={useQueryNoInvalidate_} />,
    },
    ...decisionAr?.enabled
      ? [
        {
          id: "ar-photo",
          taskType: 7,
          label: t("Augmented Reality + Photo"),
          description: t("Submit a photo with augmented reality content."),
          details: t("Players submit a photo using your augmented reality content."),
          icon: IconStandardPhoto,
          children: (
            <AugmentedRealityPhoto
              realities={realities}
              task_id={id}
              useQuery={useQueryNoInvalidate_}
            />
          ),
        },
        {
          id: "ar-video",
          taskType: 8,
          label: t("Augmented Reality + Video"),
          description: t("Submit a video with augmented reality content."),
          details: t("Players submit a video using your augmented reality content."),
          icon: IconStandardVideo,
          children: (
            <AugmentedRealityVideo
              realities={realities}
              task_id={id}
              useQuery={useQueryNoInvalidate_}
            />
          ),
        },
      ] : [],
  ], [decisionAr?.enabled, id, realities, t, useQueryNoInvalidate_]);

  const allTypes = useMemo(() => [...popularTypes, ...moreTypes], [moreTypes, popularTypes]);

  const matchesType = useCallback((option) => {
    if (selectionIntent.typeId) {
      return option.id === selectionIntent.typeId;
    }

    if (String(task_type) !== String(option.taskType)) {
      return false;
    }

    if (option.subtype === undefined) {
      return true;
    }

    if (isPromptDrivenTaskType(option.taskType)) {
      return String(option.subtype) === subtypeFromPrompt(prompt);
    }

    return String(task_subtype ?? 0) === String(option.subtype);
  }, [prompt, selectionIntent.typeId, task_subtype, task_type]);

  const selectedType = allTypes.find(matchesType)
    || allTypes.find((option) => String(option.taskType) === String(task_type) && option.subtype === 0)
    || popularTypes[0];
  const selectedMoreType = moreTypes.find((option) => option.id === selectedType?.id);

  const selectType = async (option) => {
    const shouldApplyDefaultVideoLength = option.defaultVideoLength !== undefined && !matchesType(option);
    const selectedSubtype = persistedSubtypeForTypeSelection(option);

    setSelectionIntent({ challengeId: id, typeId: option.id });

    let defaultOverlayPayload = {};
    if (option.defaultOverlay) {
      try {
        defaultOverlayPayload = await getUploadedDefaultOverlayPayload({
          assets,
          overlayMediaUuid: overlay_media_uuid,
          imageUrl: new URL(DefaultBackgroundImage, window.location.origin).toString(),
        });
      } catch (error) {
        setAlert({
          type: "error",
          message: getErrorMessage(error, "uploading your image"),
          closable: true,
        });
      }
    }
    const optionWithDefaults = Object.keys(defaultOverlayPayload).length
      ? {
        ...option,
        reset: {
          ...(option.reset || {}),
          ...defaultOverlayPayload,
        },
      }
      : option;

    const writes = getTypeSelectionWrites({
      option: optionWithDefaults,
      currentTaskType: task_type,
      selectedSubtype,
      shouldApplyDefaultVideoLength,
    });

    for (const payload of writes) {
      await setChallenge(payload);
    }
  };

  const renderSelectedContent = (option, hideHeader = false) => (
    <ChallengeTypeBody $isPopular={hideHeader}>
      {!hideHeader && (
        <ChallengeTypeHeader>
          <ChallengeTypeIcon $selected={true}>
            {option.icon ? <option.icon width="1em" height="1em" /> : null}
          </ChallengeTypeIcon>
          <ChallengeTypeMeta>
            <strong>{option.label}</strong>
            <span>{option.description}</span>
          </ChallengeTypeMeta>
        </ChallengeTypeHeader>
      )}
      <p style={{ marginTop: hideHeader ? 0 : "0.75rem" }}>{option.details}</p>
      {
        option.image
          ? (
            <ChallengeTypeExample
              alt={t("{{label}} Example", { label: option.label })}
              src={option.image}
            />
          )
          : null
      }
      {option.children}
    </ChallengeTypeBody>
  );

  return (
    <form id={`form-${id}`} onSubmit={e => e.preventDefault()}>
      <ChallengeTypesWrapper>
        <SectionLabel>{t("How Players Complete This Challenge")}</SectionLabel>
        <TypeGroupLabel>{t("Popular Types")}</TypeGroupLabel>
        {popularTypes.map((option) => {
          const checked = selectedType?.id === option.id;

          return (
            <div key={option.id}>
              <ChallengeTypeOption $selected={checked}>
                <input
                  checked={checked}
                  name="task_type"
                  onChange={() => selectType(option)}
                  type="radio"
                  value={option.id}
                />
                <ChallengeTypeIcon $selected={checked}>
                  {option.icon ? <option.icon width="1em" height="1em" /> : null}
                </ChallengeTypeIcon>
                <ChallengeTypeMeta>
                  <strong>{option.label}</strong>
                  <span>{option.description}</span>
                </ChallengeTypeMeta>
              </ChallengeTypeOption>
              {checked ? renderSelectedContent(option, true) : null}
            </div>
          );
        })}
        <TypeGroupLabel>{t("More Types")}</TypeGroupLabel>
        <MoreTypesRow>
          {moreTypes.map((option) => {
            const checked = selectedType?.id === option.id;

            return (
              <MoreTypePill
                $selected={checked}
                key={option.id}
                onClick={() => selectType(option)}
                type="button"
              >
                {option.icon ? <option.icon width="1em" height="1em" /> : null}
                {option.label}
              </MoreTypePill>
            );
          })}
        </MoreTypesRow>
        {selectedMoreType ? renderSelectedContent(selectedMoreType) : null}
      </ChallengeTypesWrapper>
    </form>
  );
};

export default TypeSection;
